import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Loader2, AlertCircle, Search, X, ChevronDown, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  car:        { label: 'Car',        color: 'bg-blue-100 text-blue-700 border-blue-200',      dot: 'bg-blue-500' },
  bike:       { label: 'Bike',       color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  ev:         { label: 'EV',         color: 'bg-green-100 text-green-700 border-green-200',    dot: 'bg-green-500' },
  commercial: { label: 'Commercial', color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
};

const EV_SUBTYPES = [
  { value: 'car',        label: 'EV Car',        icon: '🚗', desc: 'Electric cars & sedans' },
  { value: 'bike',       label: 'EV Bike',       icon: '🏍️', desc: 'Electric bikes & scooters' },
  { value: 'commercial', label: 'EV Commercial', icon: '🚛', desc: 'Electric trucks & vans' },
];

const PAGE_SIZE = 10;

// ─── Searchable model combobox ────────────────────────────────────────────────
function ModelCombobox({ models, loadingModels, value, onChange, modelSearch, setModelSearch, fetchMoreModels, hasMoreModels, disabled }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const selectedModel = models.find(m => m.id === value);

  useEffect(() => {
    const h = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loadingModels || !hasMoreModels) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) fetchMoreModels();
  }, [loadingModels, hasMoreModels, fetchMoreModels]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button type="button" disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-md bg-white text-sm transition ${
          disabled ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50' : 'border-gray-200 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
        }`}>
        <span className={selectedModel ? 'text-gray-900' : 'text-gray-400'}>
          {disabled ? 'Select EV type first' : selectedModel ? selectedModel.name : 'Select model'}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input autoFocus value={modelSearch} onChange={e => setModelSearch(e.target.value)} placeholder="Search models..."
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400" />
            </div>
          </div>
          <ul ref={listRef} onScroll={handleScroll} className="max-h-52 overflow-y-auto py-1">
            {models.length === 0 && !loadingModels && <li className="px-3 py-4 text-sm text-gray-400 text-center">No models found</li>}
            {models.map(m => (
              <li key={m.id} onClick={() => { onChange(m.id); setOpen(false); }}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center justify-between ${value === m.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}>
                <span>{m.name}</span>
                {m.brand_name && <span className="text-xs text-gray-400 ml-2">{m.brand_name}</span>}
              </li>
            ))}
            {loadingModels && <li className="px-3 py-2 text-center"><Loader2 className="h-4 w-4 animate-spin inline text-gray-400" /></li>}
            {!loadingModels && hasMoreModels && <li className="px-3 py-2 text-center text-xs text-blue-500 cursor-pointer hover:underline" onClick={fetchMoreModels}>Load more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function VariantsTab({ brands, onDataChange }) {
  const { token } = useAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // ── Dialog state ────────────────────────────────────────────────
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [variantForm, setVariantForm] = useState({ name: '', model_id: '' });

  // ── EV subtype inside dialog ─────────────────────────────────────
  const [dialogEvSubtype, setDialogEvSubtype] = useState('');

  // ── Brand filter inside dialog ───────────────────────────────────
  // When category is non-EV, user can optionally filter models by brand
  const [dialogBrandFilter, setDialogBrandFilter] = useState('');     // brand_id or ''
  const [dialogBrandSearch, setDialogBrandSearch] = useState('');     // text search for brand dropdown

  // ── Model dropdown inside dialog ─────────────────────────────────
  const [dialogModels, setDialogModels] = useState([]);
  const [dialogModelSearch, setDialogModelSearch] = useState('');
  const [dialogModelSkip, setDialogModelSkip] = useState(0);
  const [dialogModelHasMore, setDialogModelHasMore] = useState(true);
  const [loadingDialogModels, setLoadingDialogModels] = useState(false);
  const DIALOG_PAGE = 20;

  // ── Variants list ────────────────────────────────────────────────
  const [activeCategory, setActiveCategory] = useState('car');
  const [variantSearch, setVariantSearch] = useState('');
  const [variants, setVariants] = useState([]);
  const [variantSkip, setVariantSkip] = useState(0);
  const [variantHasMore, setVariantHasMore] = useState(true);
  const [variantTotal, setVariantTotal] = useState(0);
  const [loadingVariants, setLoadingVariants] = useState(false);

  const sentinelRef    = useRef(null);
  const searchDebounce = useRef(null);
  const isEVCategory   = activeCategory === 'ev';

  // ── Brands available in the dialog (filtered by active category) ──
  const dialogAvailableBrands = brands.filter(b => {
    if (b.category !== activeCategory) return false;
    if (!dialogBrandSearch.trim()) return true;
    return b.name?.toLowerCase().includes(dialogBrandSearch.trim().toLowerCase());
  });

  // ─────────────────────────────────────────────────────────────────
  // 1. Fetch dialog models
  //    For EV: filter by ev_subtype
  //    For non-EV: optionally filter by brand_id
  // ─────────────────────────────────────────────────────────────────
  const fetchDialogModels = useCallback(async (search, skip, evSubtype = '', brandId = '', append = false) => {
    setLoadingDialogModels(true);
    try {
      const params = { search: search || undefined, skip, limit: DIALOG_PAGE };
      if (evSubtype) params.ev_subtype = evSubtype;
      if (brandId)   params.brand_id   = brandId;

      const res = await axios.get(`${API}/models`, { params, ...authHeaders });
      const newModels = (res.data.models || []).map(m => ({
        ...m,
        brand_name: brands.find(b => b.id === m.brand_id)?.name || '',
      }));
      setDialogModels(prev => append ? [...prev, ...newModels] : newModels);
      setDialogModelHasMore(res.data.has_more ?? false);
    } catch { toast.error('Failed to load models'); }
    finally { setLoadingDialogModels(false); }
  }, [brands]);

  // Reset + load when dialog opens
  useEffect(() => {
    if (showDialog) {
      setDialogModelSkip(0); setDialogModelSearch('');
      setDialogEvSubtype(''); setDialogModels([]);
      setDialogBrandFilter(''); setDialogBrandSearch('');
      if (!isEVCategory) fetchDialogModels('', 0, '', '', false);
    }
  }, [showDialog]);

  // Re-fetch when model search changes
  useEffect(() => {
    if (!showDialog) return;
    const t = setTimeout(() => {
      setDialogModelSkip(0);
      fetchDialogModels(dialogModelSearch, 0, dialogEvSubtype, dialogBrandFilter, false);
    }, 300);
    return () => clearTimeout(t);
  }, [dialogModelSearch, showDialog]);

  // Re-fetch when EV subtype changes
  useEffect(() => {
    if (!showDialog || !isEVCategory) return;
    setVariantForm(f => ({ ...f, model_id: '' }));
    setDialogModelSearch(''); setDialogModelSkip(0);
    if (dialogEvSubtype) fetchDialogModels('', 0, dialogEvSubtype, '', false);
    else setDialogModels([]);
  }, [dialogEvSubtype, showDialog, isEVCategory]);

  // Re-fetch when brand filter changes (non-EV)
  useEffect(() => {
    if (!showDialog || isEVCategory) return;
    setVariantForm(f => ({ ...f, model_id: '' }));
    setDialogModelSearch(''); setDialogModelSkip(0);
    fetchDialogModels('', 0, '', dialogBrandFilter, false);
  }, [dialogBrandFilter, showDialog, isEVCategory]);

  const fetchMoreDialogModels = useCallback(() => {
    const next = dialogModelSkip + DIALOG_PAGE;
    setDialogModelSkip(next);
    fetchDialogModels(dialogModelSearch, next, dialogEvSubtype, dialogBrandFilter, true);
  }, [dialogModelSkip, dialogModelSearch, dialogEvSubtype, dialogBrandFilter, fetchDialogModels]);

  // ─────────────────────────────────────────────────────────────────
  // 2. Fetch variants list
  // ─────────────────────────────────────────────────────────────────
  const fetchVariants = useCallback(async (category, search, skip, append = false) => {
    setLoadingVariants(true);
    try {
      const categoryBrandIds = new Set(brands.filter(b => b.category === category).map(b => b.id));
      if (categoryBrandIds.size === 0) {
        if (!append) { setVariants([]); setVariantTotal(0); setVariantHasMore(false); }
        return;
      }

      let categoryModels = [], modelSkip = 0;
      while (true) {
        const res = await axios.get(`${API}/models`, { params: { skip: modelSkip, limit: 50 }, ...authHeaders });
        const page = res.data.models || [];
        categoryModels = [...categoryModels, ...page.filter(m => categoryBrandIds.has(m.brand_id))];
        if (!res.data.has_more) break;
        modelSkip += 50;
      }
      if (categoryModels.length === 0) {
        if (!append) { setVariants([]); setVariantTotal(0); setVariantHasMore(false); }
        return;
      }

      const modelMap = Object.fromEntries(categoryModels.map(m => [m.id, m]));
      const modelIds = categoryModels.map(m => m.id);

      let allVariants = [];
      const BATCH = 5;
      for (let i = 0; i < modelIds.length; i += BATCH) {
        const batch = modelIds.slice(i, i + BATCH);
        const results = await Promise.all(
          batch.map(mid => axios.get(`${API}/variants`, { params: { model_id: mid }, ...authHeaders }).then(r => r.data.variants || []).catch(() => []))
        );
        results.forEach(group => { allVariants = [...allVariants, ...group]; });
      }

      allVariants.sort((a, b) => {
        const da = a.created_at || '', db = b.created_at || '';
        if (da && db) return db.localeCompare(da);
        return (a.name || '').localeCompare(b.name || '');
      });

      let filtered = allVariants;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        filtered = allVariants.filter(v => {
          const model = modelMap[v.model_id];
          const brand = brands.find(b => b.id === model?.brand_id);
          return v.name?.toLowerCase().includes(q) || model?.name?.toLowerCase().includes(q) || brand?.name?.toLowerCase().includes(q);
        });
      }

      const total    = filtered.length;
      const pageData = filtered.slice(skip, skip + PAGE_SIZE);
      const enriched = pageData.map(v => {
        const model = modelMap[v.model_id];
        const brand = brands.find(b => b.id === model?.brand_id);
        return { ...v, model_name: model?.name || 'Unknown', brand_name: brand?.name || 'Unknown' };
      });

      setVariantTotal(total); setVariantHasMore((skip + PAGE_SIZE) < total);
      setVariants(prev => append ? [...prev, ...enriched] : enriched);
    } catch (err) { console.error(err); toast.error('Failed to load variants'); }
    finally { setLoadingVariants(false); }
  }, [brands]);

  useEffect(() => {
    if (brands.length === 0) return;
    setVariants([]); setVariantSkip(0); setVariantHasMore(true);
    fetchVariants(activeCategory, variantSearch, 0, false);
  }, [activeCategory, brands]);

  useEffect(() => {
    if (brands.length === 0) return;
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setVariants([]); setVariantSkip(0); setVariantHasMore(true);
      fetchVariants(activeCategory, variantSearch, 0, false);
    }, 350);
    return () => clearTimeout(searchDebounce.current);
  }, [variantSearch]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && variantHasMore && !loadingVariants) {
        const next = variantSkip + PAGE_SIZE; setVariantSkip(next);
        fetchVariants(activeCategory, variantSearch, next, true);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [variantHasMore, loadingVariants, variantSkip, activeCategory, variantSearch, fetchVariants]);

  // ─────────────────────────────────────────────────────────────────
  // 3. CRUD
  // ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!variantForm.name.trim() || !variantForm.model_id) { toast.error('Please fill all required fields'); return; }
    if (isEVCategory && !dialogEvSubtype) { toast.error('Please select the EV type (Car, Bike, or Commercial)'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/superadmin/variants/${editingItem}`, variantForm, authHeaders);
        toast.success('Variant updated successfully');
      } else {
        await axios.post(`${API}/superadmin/variants`, variantForm, authHeaders);
        toast.success('Variant created successfully');
      }
      setShowDialog(false); resetForm();
      setVariants([]); setVariantSkip(0); setVariantHasMore(true);
      fetchVariants(activeCategory, variantSearch, 0, false);
      onDataChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save variant'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (variantId) => {
    if (!window.confirm('Delete this variant?')) return;
    try {
      await axios.delete(`${API}/superadmin/variants/${variantId}`, authHeaders);
      toast.success('Variant deleted');
      setVariants(prev => prev.filter(v => v.id !== variantId));
      setVariantTotal(t => t - 1); onDataChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const startEdit = (variant) => { setVariantForm({ name: variant.name, model_id: variant.model_id }); setEditingItem(variant.id); setShowDialog(true); };

  const resetForm = () => {
    setVariantForm({ name: '', model_id: '' }); setEditingItem(null);
    setDialogEvSubtype(''); setDialogModels([]); setDialogModelSearch(''); setDialogModelSkip(0);
    setDialogBrandFilter(''); setDialogBrandSearch('');
  };

  const handleDialogClose = (open) => { if (!open) resetForm(); setShowDialog(open); };
  const catCfg = CATEGORY_CONFIG[activeCategory];
  const modelComboboxDisabled = isEVCategory && !dialogEvSubtype;

  // ─────────────────────────────────────────────────────────────────
  // 4. Render
  // ─────────────────────────────────────────────────────────────────
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>
              Variant Management
              <span className="text-sm font-normal text-gray-400 ml-2">({variantTotal} in {catCfg.label})</span>
            </CardTitle>

            <Dialog open={showDialog} onOpenChange={handleDialogClose}>
              <DialogTrigger asChild>
                <Button className="bg-primary-500 text-black hover:bg-primary-600" data-testid="add-variant-btn" onClick={resetForm}>
                  <Plus className="mr-2 h-4 w-4" /> Add Variant
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {editingItem ? 'Edit Variant' : 'Add New Variant'}
                    {isEVCategory && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <Zap className="h-3 w-3" /> EV
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-5">

                  {/* ── EV SUBTYPE CARDS (only for EV category) ── */}
                  {isEVCategory && (
                    <div>
                      <Label className="mb-2 block">
                        EV Type *
                        <span className="ml-2 text-xs text-gray-400 font-normal">Select to filter brands &amp; models</span>
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        {EV_SUBTYPES.map(sub => {
                          const isSelected = dialogEvSubtype === sub.value;
                          return (
                            <button key={sub.value} type="button" onClick={() => setDialogEvSubtype(sub.value)}
                              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all ${isSelected ? 'border-green-500 bg-green-50 text-green-700 shadow-sm' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-green-300 hover:bg-green-50/50'}`}>
                              <span className="text-xl leading-none">{sub.icon}</span>
                              <span className="text-xs font-semibold leading-tight">{sub.label}</span>
                              <span className="text-[10px] text-gray-400 leading-tight">{sub.desc}</span>
                              {isSelected && <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded font-bold">✓ Selected</span>}
                            </button>
                          );
                        })}
                      </div>
                      {dialogEvSubtype ? (
                        <p className="text-xs text-green-600 mt-1.5">✓ Showing brands &amp; models for <strong>{EV_SUBTYPES.find(s => s.value === dialogEvSubtype)?.label}</strong> only.</p>
                      ) : (
                        <p className="text-xs text-amber-600 mt-1.5">⚠️ Select an EV type above to load matching brands &amp; models.</p>
                      )}
                    </div>
                  )}

                  {/* ── BRAND FILTER (non-EV: optional brand filter; EV: shown after subtype selection) ── */}
                  {(!isEVCategory || dialogEvSubtype) && (
                    <div>
                      <Label className="mb-1 block">
                        Filter by Brand
                        <span className="ml-2 text-xs text-gray-400 font-normal">(optional — narrows model list)</span>
                      </Label>
                      <div className="border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-400 transition">
                        {/* Search input */}
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
                          <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <input type="text" value={dialogBrandSearch} onChange={e => setDialogBrandSearch(e.target.value)}
                            placeholder={`Search ${catCfg.label} brands...`}
                            className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400 text-gray-700" autoComplete="off" />
                          {dialogBrandSearch && <button type="button" onClick={() => setDialogBrandSearch('')} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>}
                        </div>

                        {/* Brand list */}
                        <div className="max-h-36 overflow-y-auto">
                          {/* "All brands" option */}
                          <button type="button" onClick={() => { setDialogBrandFilter(''); setDialogBrandSearch(''); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50 ${!dialogBrandFilter ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600'}`}>
                            <span className="text-gray-400 text-xs italic">All {catCfg.label} brands</span>
                            {!dialogBrandFilter && <span className="ml-auto text-blue-600 text-xs font-bold">✓</span>}
                          </button>
                          {dialogAvailableBrands.map(brand => {
                            // For EV + subtype chosen: only show brands that match the subtype
                            if (isEVCategory && dialogEvSubtype) {
                              if (Array.isArray(brand.ev_subtypes) && brand.ev_subtypes.length > 0) {
                                if (!brand.ev_subtypes.includes(dialogEvSubtype)) return null;
                              } else if (brand.sub_category && brand.sub_category !== dialogEvSubtype) {
                                return null;
                              }
                            }
                            const isSelected = dialogBrandFilter === brand.id;
                            return (
                              <button key={brand.id} type="button"
                                onClick={() => { setDialogBrandFilter(brand.id); setDialogBrandSearch(''); }}
                                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50 ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}>
                                {brand.logo ? (
                                  <img src={brand.logo} alt={brand.name} className="w-5 h-5 object-contain rounded border border-gray-200 bg-white p-0.5 flex-shrink-0" />
                                ) : (
                                  <div className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold flex-shrink-0">{brand.name?.charAt(0)}</div>
                                )}
                                <span className="flex-1 font-medium">{brand.name}</span>
                                {isSelected && <span className="text-blue-600 text-xs font-bold">✓</span>}
                              </button>
                            );
                          })}
                        </div>

                        {/* Selected brand preview */}
                        {dialogBrandFilter && (() => {
                          const sel = brands.find(b => b.id === dialogBrandFilter);
                          return sel ? (
                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-blue-50 border-t border-blue-100">
                              <span className="text-xs text-blue-700 font-semibold">Brand: {sel.name}</span>
                              <button type="button" onClick={() => setDialogBrandFilter('')} className="text-xs text-gray-400 hover:text-red-500">✕ Clear</button>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  )}

                  {/* ── MODEL SELECTOR ── */}
                  <div>
                    <Label className="mb-1 block">Model <span className="text-red-500">*</span></Label>
                    <ModelCombobox
                      models={dialogModels} loadingModels={loadingDialogModels}
                      value={variantForm.model_id}
                      onChange={val => setVariantForm(f => ({ ...f, model_id: val }))}
                      modelSearch={dialogModelSearch} setModelSearch={setDialogModelSearch}
                      fetchMoreModels={fetchMoreDialogModels} hasMoreModels={dialogModelHasMore}
                      disabled={modelComboboxDisabled}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {isEVCategory && !dialogEvSubtype ? 'Choose an EV type first to see models.' : 'Type to search models by name.'}
                    </p>
                  </div>

                  {/* ── VARIANT NAME ── */}
                  <div>
                    <Label className="mb-1 block">Variant Name <span className="text-red-500">*</span></Label>
                    <Input value={variantForm.name} onChange={e => setVariantForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., SX(O)" data-testid="variant-name-input" onKeyDown={e => e.key === 'Enter' && handleSave()} />
                  </div>

                  <Button onClick={handleSave} disabled={saving} className="w-full bg-green-500 hover:bg-green-600 text-white" data-testid="save-variant-btn">
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    {editingItem ? 'Update Variant' : 'Create Variant'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Category tabs */}
          <div className="flex gap-2 flex-wrap">
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
              <button key={key} onClick={() => { setActiveCategory(key); setVariantSearch(''); }}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all capitalize ${activeCategory === key ? `${cfg.color} border-current` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'}`}>
                {cfg.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input value={variantSearch} onChange={e => setVariantSearch(e.target.value)} placeholder="Search by variant, model, or brand name..." className="pl-9 border-gray-200 focus:border-blue-400" />
            {variantSearch && <button onClick={() => setVariantSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {variants.length === 0 && loadingVariants ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-sm text-gray-400">Loading {catCfg.label} variants...</p>
          </div>
        ) : variants.length === 0 && !loadingVariants ? (
          <div className="text-center py-12 text-gray-400">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="font-medium">{variantSearch ? `No variants found matching "${variantSearch}" in ${catCfg.label}` : `No variants found for ${catCfg.label}. Add your first variant!`}</p>
            {variantSearch && <button onClick={() => setVariantSearch('')} className="mt-2 text-sm text-blue-500 hover:underline">Clear search</button>}
          </div>
        ) : (
          <div className="space-y-2">
            {variants.map((variant, idx) => (
              <div key={variant.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors" data-testid={`variant-row-${idx}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${catCfg.dot}`} />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-800 truncate">{variant.name}</h3>
                    <p className="text-sm text-gray-500 truncate">{variant.brand_name} &rsaquo; {variant.model_name}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 ml-3">
                  <Button variant="outline" size="sm" onClick={() => startEdit(variant)} data-testid={`edit-variant-btn-${idx}`}><Edit className="h-4 w-4" /></Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(variant.id)} data-testid={`delete-variant-btn-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}

            <div ref={sentinelRef} className="h-4" />
            {loadingVariants && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /><span className="ml-2 text-sm text-gray-400">Loading more variants...</span></div>}
            {!variantHasMore && variants.length > 0 && <p className="text-center text-xs text-gray-400 py-3">All {variantTotal} variants loaded</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}